/** Copyright © 2013-2021 DataYes, All Rights Reserved. */

/* eslint no-underscore-dangle: 0 */
import { isArray, get, startsWith, some, map } from 'lodash';
import { FILE_URL } from '../login-register/utils/config';
import defaultAvatar from './images/avatar.png';

export const getConfig = (key: any, endpoint = '') => {
  let config = get((window as any)._config, key);
  // 如果endpoint是全路径，则直接返回endpoint
  if (startsWith(endpoint, 'http')) {
    return endpoint;
  }
  if (config && endpoint) {
    config += endpoint;
  }
  return config;
};
export const getBase = () => {
  const { origin } = window.location;
  const baseTag = document.head.querySelector('base');
  const base = (baseTag && baseTag.href) || `${origin}/`;
  return base.replace(origin, '');
};
export const getActiveNav = (navs: any[]) => {
  const { href, origin } = window.location;
  const baseTag = document.head.querySelector('base');
  const base = (baseTag && baseTag.href) || `${origin}/`;
  const fragment = href.replace(base, '');
  const fragmentArray = fragment.split('/');
  let mainNavKey: any;
  let subNavKey;
  const newArr: any = [];
  if (!fragment || fragment === href) {
    return {
      mainNavKey: 'home',
      subNavKey,
    };
  }
  fragmentArray.some((value) => {
    if (value) {
      // 移除params和hash
      const cleanValue = value.split('?')[0].split('#')[0];
      // 如果cleanValue为空，则终止
      if (!cleanValue) return true;
      // 如果是数字开头，则停止循环，用于处理类似url：details/announcement/24784616
      if (/^\d.+/.test(cleanValue)) return true;
      newArr.push(cleanValue);
    }
    return false;
  });
  const key = newArr.join('/') || 'home';
  navs.some((mainNav) => {
    const children = (mainNav as any).children;
    let isMatch = false;
    if (children) {
      // 如果存在二级导航，则从二级导航开始找起，找到匹配则可确定主导航，然后终止寻找
      isMatch = children.some((subNav: any) => {
        let flag = false;
        if (isArray(subNav.key)) {
          flag = some(subNav.key, (k) => {
            if (key.startsWith(k)) {
              subNavKey = k;
              return true;
            }
            return false;
          });
        } else if (
          (!subNav.exact && key.startsWith(subNav.key)) ||
          (subNav.exact && key === subNav.key)
        ) {
          subNavKey = subNav.key;
          flag = true;
        }
        if (flag) {
          mainNavKey = (mainNav as any).key;
        }
        return flag;
      });
    }
    if (!isMatch) {
      if ((mainNav as any).key === key) {
        // 如果无二级导航，则在主导航寻找，找到即停止
        mainNavKey = key;
        isMatch = true;
      } else if ((mainNav as any).key === key.split('/')[0]) {
        // 处理dashboard/xxxx类似的路由，未配置二级但需高亮的一级导航
        mainNavKey = key.split('/')[0];
        isMatch = true;
      }
    }
    return isMatch;
  });
  // 硬性匹配
  switch (key) {
    case 'details/feed':
      mainNavKey = 'home';
      break;
    case 'details/announcement':
    case 'details/news':
      mainNavKey = 'discovery';
      break;
    case 'details/report':
      mainNavKey = 'report';
      break;
    case 'landing/monitor_detail':
      mainNavKey = 'data';
      break;
    default:
  }
  const subNavs = navs.find((nav) => (nav as any).key === mainNavKey)?.children;
  // 如果无匹配，默认显示更多
  return { subNavs, mainNavKey: mainNavKey || 'navigation', subNavKey, key };
};
export function safeParse(text: any) {
  try {
    const parsed = JSON.parse(text);
    return parsed;
  } catch (e) {
    // console.log(e);
    return null;
  }
}
export const formatUser = (identity: any) => ({
  userAvatar: getUserAvatar(identity),
  username: getUserName(identity),
  roleList: getRoleList(identity),
  isPersonal: getIsPersonal(identity),
  isAnonymous: getIsAnonymous(identity),
  company: get(identity, 'user.domainName'),
  principalName: get(identity, 'user.principalName'),
  tenantLogo: get(identity, 'user.tenantLogo'),
});
function getUserAvatar(identity: any) {
  const imageId = get(identity, 'user.imageId');

  if (imageId) {
    return `${FILE_URL}/v1/AUTH_datayes/${imageId}`;
  }
  return defaultAvatar;
}
// 获取显示的用户名
function getUserName(identity: any) {
  const user = identity.user || {};
  let name = user.name || '';
  if (user.domain === 'wmcloud.com' && identity.personalUser) {
    // 个人用户
    // 用户名称显示顺序为：微信昵称 > 微博昵称 > 手机 > 邮箱 > 昵称 > 用户名
    const pu = identity.personalUser;
    const pi = pu.passportInfo;
    if (pi.wechat) {
      name = pi.wechat.nickname || pi.wechat;
    } else if (pi.weibo) {
      name = pi.weibo;
    } else if (pi.mobile) {
      name = pi.mobile;
    } else if (pi.mail && !pi.mailPending) {
      name = pi.mail;
    } else if (pu.nickName) {
      name = pu.nickName;
    } else {
      name = pi.username;
    }
  } else if (user.surname || user.givenName) {
    // 企业用户
    // 如果存在姓或名，则拼接即可
    name = (user.surname || '') + (user.givenName || '');
  }
  return name || '';
}
// 获取角色列表
function getRoleList(identity: any) {
  const roleList = get(identity, 'user.rolesList') || [];
  return map(roleList, (role) => role.roleName);
}
// 判断是否是个人用户
function getIsPersonal(identity: any) {
  if (
    identity &&
    identity.user &&
    identity.user.domain === 'wmcloud.com' &&
    identity.personalUser
  ) {
    return true;
  }
  return false;
}
// 判断是否是游客用户
function getIsAnonymous(identity: any) {
  return !!get(identity, 'user.anonymousUser');
}
// 获取萝卜v2地址
export const getRoboUrl = (endpoint = '') => {
  let roboUrl = `${(window as any)._config.rrp_url}`.replace('robor', 'robo'); // eslint-disable-line no-underscore-dangle
  // ci qa环境
  if (roboUrl.includes('wmcloud-qa.com')) {
    roboUrl = (window as any)._config.rrp_url; // eslint-disable-line no-underscore-dangle
  }
  if (endpoint) {
    roboUrl += endpoint;
  }
  return roboUrl;
};

export const type2TagName = {
  stock: {
    tagName: '股票详情',
  },
  fund: {
    tagName: '基金详情',
  },
  bond: {
    tagName: '债券详情',
  },
  industry: {
    tagName: '行业详情',
  },
  product: {
    tagName: '产品详情',
    hasContent: false,
  },
};
